import { onBeforeUnmount, ref } from "vue";

export const CARD_DEFAULT_WIDTH = 497;

type UseResizableCardWidthOptions = {
  enabled?: boolean;
  storageKey?: string;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
};

export function useResizableCardWidth(
  options: UseResizableCardWidthOptions = {},
) {
  const {
    enabled = false,
    storageKey = "card_width",
    defaultWidth = CARD_DEFAULT_WIDTH,
    minWidth = 400,
    maxWidth = 550,
  } = options;

  if (!enabled) {
    const modalWidth = ref<number>(defaultWidth);
    const isResizing = ref(false);
    const resizerStyle = {
      pointerEvents: "none" as const,
      cursor: "default" as const,
    };
    const startResize = (_e: MouseEvent) => {};
    const stopResize = () => {};

    return { modalWidth, isResizing, startResize, stopResize, resizerStyle };
  }

  const getInitialWidth = () => {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return defaultWidth;
    try {
      const parsed = JSON.parse(raw) as unknown;
      return typeof parsed === "number" ? parsed : defaultWidth;
    } catch {
      return defaultWidth;
    }
  };

  const modalWidth = ref<number>(getInitialWidth());

  const isResizing = ref(false);
  const resizerStyle = undefined;

  const resize = (e: MouseEvent) => {
    if (!isResizing.value) return;
    const windowRightEdge = window.innerWidth;
    const newWidth = windowRightEdge - e.clientX;
    modalWidth.value = Math.min(Math.max(newWidth, minWidth), maxWidth);
  };

  const stopResize = () => {
    if (!isResizing.value) return;
    isResizing.value = false;
    localStorage.setItem(storageKey, JSON.stringify(modalWidth.value));
    document.removeEventListener("mousemove", resize);
    document.removeEventListener("mouseup", stopResize);
  };

  const startResize = (_e: MouseEvent) => {
    isResizing.value = true;
    document.addEventListener("mousemove", resize);
    document.addEventListener("mouseup", stopResize);
  };

  onBeforeUnmount(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(modalWidth.value));
    } catch {
      // ignore storage failures
    }
    stopResize();
  });

  return { modalWidth, isResizing, startResize, stopResize, resizerStyle };
}
